/**
 * Fechas y períodos.
 *
 * Todo son cadenas `YYYY-MM-DD`. No se usa `Date` para aritmética de
 * calendario: `new Date('2026-03-01')` es medianoche **UTC**, y en Costa Rica
 * (UTC−6) eso es el 28 de febrero. Ese error de un día es exactamente el tipo
 * de cosa que corrompe un mes de cobros.
 */

export type IsoDate = string; // YYYY-MM-DD

const ISO_RE = /^(\d{4})-(\d{2})-(\d{2})$/;

export function parseIso(d: IsoDate): { y: number; m: number; d: number } {
  const m = ISO_RE.exec(d);
  if (!m) throw new RangeError(`fecha inválida: ${d}`);
  return { y: Number(m[1]), m: Number(m[2]), d: Number(m[3]) };
}

export function isIsoDate(d: unknown): d is IsoDate {
  return typeof d === 'string' && ISO_RE.test(d);
}

function pad(n: number, width = 2): string {
  return String(n).padStart(width, '0');
}

export function toIso(y: number, m: number, d: number): IsoDate {
  return `${pad(y, 4)}-${pad(m)}-${pad(d)}`;
}

/** Meses absolutos desde el año 0. Sirve para restar dos períodos. */
export function monthIndex(d: IsoDate): number {
  const { y, m } = parseIso(d);
  return y * 12 + (m - 1);
}

export function daysInMonth(y: number, m: number): number {
  return new Date(Date.UTC(y, m, 0)).getUTCDate();
}

/** Primer día del mes de `d`. Es la forma canónica de un `period_start`. */
export function startOfMonth(d: IsoDate): IsoDate {
  const { y, m } = parseIso(d);
  return toIso(y, m, 1);
}

export function endOfMonth(d: IsoDate): IsoDate {
  const { y, m } = parseIso(d);
  return toIso(y, m, daysInMonth(y, m));
}

/** Suma meses conservando el día, recortándolo si el mes destino es más corto. */
export function addMonths(d: IsoDate, k: number): IsoDate {
  const { y, m, d: day } = parseIso(d);
  const total = y * 12 + (m - 1) + k;
  const ny = Math.floor(total / 12);
  const nm = (total % 12) + 1;
  return toIso(ny, nm, Math.min(day, daysInMonth(ny, nm)));
}

export function addDays(d: IsoDate, k: number): IsoDate {
  const { y, m, d: day } = parseIso(d);
  const t = Date.UTC(y, m - 1, day + k);
  const dt = new Date(t);
  return toIso(dt.getUTCFullYear(), dt.getUTCMonth() + 1, dt.getUTCDate());
}

export function compareIso(a: IsoDate, b: IsoDate): number {
  return a < b ? -1 : a > b ? 1 : 0;
}

/** Lunes de la semana de `d` — el calendario de visitas siempre arranca en lunes. */
export function startOfWeek(d: IsoDate): IsoDate {
  const { y, m, d: day } = parseIso(d);
  const dow = new Date(Date.UTC(y, m - 1, day)).getUTCDay(); // 0=domingo … 6=sábado
  const offset = dow === 0 ? -6 : 1 - dow; // retrocede hasta el lunes
  return addDays(d, offset);
}

/** `[from, to]` con `to` nulo = abierto hacia el futuro. */
export function withinRange(d: IsoDate, from: IsoDate, to: IsoDate | null | undefined): boolean {
  if (compareIso(d, from) < 0) return false;
  return to == null || compareIso(d, to) <= 0;
}

export function rangesOverlap(
  aFrom: IsoDate, aTo: IsoDate | null | undefined,
  bFrom: IsoDate, bTo: IsoDate | null | undefined,
): boolean {
  const aEnd = aTo ?? '9999-12-31';
  const bEnd = bTo ?? '9999-12-31';
  return compareIso(aFrom, bEnd) <= 0 && compareIso(bFrom, aEnd) <= 0;
}

/* ── Formato ──────────────────────────────────────────────────────────────── */

const MONTHS = [
  'enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio',
  'julio', 'agosto', 'setiembre', 'octubre', 'noviembre', 'diciembre',
] as const;

const MONTHS_SHORT = [
  'ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'set', 'oct', 'nov', 'dic',
] as const;

/** `2026-08-01` → `"agosto 2026"`. En Costa Rica el noveno mes es *setiembre*. */
export function periodLabel(d: IsoDate): string {
  const { y, m } = parseIso(d);
  return `${MONTHS[m - 1]} ${y}`;
}

/** `2026-08-01` → `"ago 26"`. Para columnas de tabla. */
export function periodLabelShort(d: IsoDate): string {
  const { y, m } = parseIso(d);
  return `${MONTHS_SHORT[m - 1]} ${pad(y % 100)}`;
}

/** `2026-08-12` → `"12/08/2026"`. */
export function dateLabel(d: IsoDate): string {
  const { y, m, d: day } = parseIso(d);
  return `${pad(day)}/${pad(m)}/${y}`;
}

/** `2026-08-12` → `"12 de agosto de 2026"`. */
export function dateLabelLong(d: IsoDate): string {
  const { y, m, d: day } = parseIso(d);
  return `${day} de ${MONTHS[m - 1]} de ${y}`;
}

/** Hoy en la zona horaria del negocio, no en la del servidor. */
export function today(timeZone = 'America/Costa_Rica', now: Date = new Date()): IsoDate {
  // `en-CA` produce YYYY-MM-DD, que es justo la forma canónica.
  return new Intl.DateTimeFormat('en-CA', {
    timeZone, year: 'numeric', month: '2-digit', day: '2-digit',
  }).format(now);
}

export function nowIso(now: Date = new Date()): string {
  return now.toISOString();
}
